import { useMutation, useQuery } from "@tanstack/react-query";
import isPes from "src/helpers/isPes";
import newApi from "src/newApi";
import api from "src/newApi";

import { queryClient } from "src/queryClient";
import options from "./defaultOptions";

export function useGetCourses() {
	return useQuery({
		queryKey: ["courses", isPes() ? "pes" : "basic"],

		queryFn: async () => {
			const res = await api.getUserCourses();
			return res
				.filter((c) => (isPes() ? c.type === "pes" : c.type !== "pes"))
				.sort((a, b) => a.updatedAt - b.updatedAt);
		},

		...options,
	});
}

export function useGetCourse(id) {
	return useQuery({
		queryKey: ["courses", id],

		queryFn: async () => {
			return api.getUserCourse(id);
		},

		...options,
		gcTime: 60 * 1000,
	});
}

export function useEditCourse() {
	return useMutation({
		mutationFn: ({ id, ...details }) => {
			return api.editCourse(id, details);
		},
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: ["courses"] });
			queryClient.invalidateQueries({ queryKey: ["folder-items"] });
			queryClient.invalidateQueries({ queryKey: ["folder"] });
		},
	});
}

export function useRemoveCourses() {
	return useMutation({
		mutationFn: (ids) => {
			return api.removeCourses(ids);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useCreateCourse() {
	return useMutation({
		mutationFn: ({ name, filters, folderIds }) => {
			return api.generateCourse({
				name,
				filters,
				folderIds,
			});
		},
		onSuccess: () => {
			queryClient.removeQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: ["examPreviewDetails"] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useCopyCourse() {
	return useMutation({
		mutationFn: (body) => {
			return api.copyCourse(body);
		},
		onSuccess: () => {
			queryClient.removeQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: ["examPreviewDetails"] });
		},
	});
}

export function useCoursesTemplates(filters) {
	return useQuery({
		queryKey: ["courses-templates", filters],

		queryFn: async () => {
			return api.getCoursesTemplates(filters);
		},

		...options,
	});
}

export function useGenerateCourseFromTemplate() {
	return useMutation({
		mutationFn: ({ templateId, folderIds }) => {
			return api.generateCourseFromTemplate(templateId, folderIds);
		},
		onSuccess: () => {
			queryClient.removeQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: ["examPreviewDetails"] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useResetSection() {
	return useMutation({
		mutationFn: ({ sectionId, courseId }) => {
			return api.resetSection(sectionId, courseId);
		},
		onSuccess: async (_data, variables) => {
			await queryClient.refetchQueries({
				queryKey: ["courses", `${variables.courseId}`],
			});
		},
	});
}

export function useGetLesson() {
	return useMutation({
		mutationFn: async ({ courseId, lessonId }) => {
			const lesson = await newApi.getUserLesson(courseId, lessonId);
			return lesson.itemId;
		},
		onSuccess: async (data, variables) => {
			if (data) {
				queryClient.removeQueries({ queryKey: ["courses"] });
			} else {
				await queryClient.refetchQueries({
					queryKey: ["courses", `${variables.courseId}`],
				});
			}
		},
	});
}

export function useUpdateLesson() {
	return useMutation({
		mutationFn: async ({ courseId, lessonId, data }) => {
			await newApi.patchLesson(courseId, lessonId, data);
		},
		onSuccess: async (_data, params) => {
			await queryClient.refetchQueries({
				queryKey: ["courses", `${params.courseId}`],
			});
		},
	});
}

export function useReportCourseReservation() {
	return useMutation({
		mutationFn: async (body) => {
			await newApi.reportCourseReservation(body);
		},
	});
}
